import axios, { Axios, AxiosError, InternalAxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../config";
import { Alert, AlertAuditLogResponse, AlertHistoryResponse, AlertType, AuditEventSearchResponse, ChangePasswordRequest, CreateManualSalesActivityRequest, CreateManualSalesActivityResponse, CreateNCAPIAccountRequest, CreateUserRequest, DashboardStats, DomainInfo, GetListingActivityRequest, GetListingActivityResponse, GetListingsResponse, GetNotificationsResponse, InboundEmail, InboundEmailAttachment, Job, JobStatus, ListAlertsRequest, ListAlertsResponse, ListAlertTypesResponse, ListDomainLeadsRequest, ListDomainLeadsResponse, ListDomainRenewalBlacklistRequest, ListDomainRenewalBlacklistResponse, ListEmailsRequest, ListEmailsResponse, ListJobsRequest, ListJobsResponse, ListNCAftermarketBidsRequest, ListNCAftermarketBidsResponse, ListNCAftermarketSalesRequest, ListNCAftermarketSalesResponse, ListNCAPIAccountsResponse, ListPortfolioRequest, ListPortfolioResponse, ListRulesResponse, ListTagsResponse, ListUsersResponse, Listing, LoginRequest, NCAftermarketSale, NCAPIAccount, SetDomainAutoRenewRequest, SetDomainAutoRenewResponse, SetDomainTagsRequest, TokenResponse, UpdateAlertTypeRequest, UpdateNCAPIAccountNotesRequest, UpdateNCAPIAccountStatusRequest, UpdateNCAPIAccountTokenRequest, UpdateUserRequest, UploadSalesHistoryRequest, UploadSalesHistoryResponse, User, alert, alertAuditLogResponse, alertHistoryResponse, alertType, auditEventSearchResponse, createManualSalesActivityResponse, dashboardStats, domainInfo, getListingActivityResponse, getListingsResponse, getNotificationsResponse, inboundEmail, job, listAlertsResponse, listAlertTypesResponse, listDomainLeadsResponse, listDomainRenewalBlacklistResponse, listEmailsResponse, listJobsResponse, listNCAftermarketBidsResponse, listNCAftermarketSalesResponse, listNCAPIAccountsResponse, listPortfolioResponse, listRulesResponse, listTagsResponse, listUsersResponse, listing, ncAftermarketSale, ncAPIAccount, setDomainAutoRenewResponse, tokenResponse, uploadSalesHistoryResponse, user } from "./models";
import { Moment } from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const REFRESH_TOKEN_STORAGE_KEY = 'parity.refreshToken';

interface Pagination {
    page: number;
    pageSize: number;
    token?: string;
}

/**
 * Internal axios config flag used to opt-out of the automatic refresh/401
 * retry flow (e.g. for the login/refresh/logout calls themselves). Stored on
 * the config object rather than as an HTTP header so it never hits the wire
 * (and doesn't need to be in CORS Allow-Headers).
 */
type AuthRequestConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
    _skipAuthRefresh?: boolean;
};

class API {
    instance: Axios
    private accessToken: string | null = null;
    private refreshToken: string | null = null;
    private refreshPromise: Promise<string | null> | null = null;
    private onUnauthorized: (() => void) | null = null;

    constructor() {
        this.instance = axios.create({
            baseURL: API_BASE_URL
        });

        try {
            this.refreshToken = window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        } catch {
            this.refreshToken = null;
        }

        this.instance.interceptors.request.use((config) => {
            const cfg = config as AuthRequestConfig;
            if (cfg._skipAuthRefresh) return cfg;
            if (this.accessToken) {
                cfg.headers = cfg.headers ?? {};
                cfg.headers['Authorization'] = `Bearer ${this.accessToken}`;
            }
            return cfg;
        });

        this.instance.interceptors.response.use(
            (resp) => resp,
            async (error: AxiosError) => {
                const original = error.config as AuthRequestConfig | undefined;
                if (!original || error.response?.status !== 401 || original._retry || original._skipAuthRefresh) {
                    return Promise.reject(error);
                }

                original._retry = true;
                let newAccess: string | null;
                try {
                    newAccess = await this.refreshAccessToken();
                } catch {
                    // Network error during refresh — don't clear tokens
                    return Promise.reject(error);
                }
                if (!newAccess) {
                    this.clearTokens();
                    this.onUnauthorized?.();
                    return Promise.reject(error);
                }
                original.headers = original.headers ?? {} as any;
                (original.headers as any)['Authorization'] = `Bearer ${newAccess}`;
                return this.instance.request(original);
            }
        );
    }

    setUnauthorizedHandler(handler: (() => void) | null) {
        this.onUnauthorized = handler;
    }

    setAccessToken(token: string | null) {
        this.accessToken = token;
    }

    getAuthHeaders(): Record<string, string> {
        return this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {};
    }

    setRefreshToken(token: string | null) {
        this.refreshToken = token;
        try {
            if (token) {
                window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
            } else {
                window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            }
        } catch {
            // ignore storage errors (private mode, quota, etc.)
        }
    }

    getRefreshToken(): string | null {
        return this.refreshToken;
    }

    clearTokens() {
        this.setAccessToken(null);
        this.setRefreshToken(null);
    }

    /**
     * Issue a refresh request, coalescing concurrent calls so only one is in
     * flight at a time. Returns the new access token, or null on auth failure.
     * Throws on network errors so callers can distinguish "unauthorized" from
     * "server unreachable".
     */
    private refreshAccessToken(): Promise<string | null> {
        if (this.refreshPromise) return this.refreshPromise;
        const refresh = this.refreshToken;
        if (!refresh) return Promise.resolve(null);

        this.refreshPromise = (async () => {
            try {
                const resp = await this.instance.post('/v1/auth/refresh',
                    { refreshToken: refresh },
                    { _skipAuthRefresh: true } as AuthRequestConfig
                );
                const parsed = tokenResponse.parse(resp.data);
                this.setAccessToken(parsed.accessToken);
                this.setRefreshToken(parsed.refreshToken);
                return parsed.accessToken;
            } catch (err: any) {
                // Only return null (auth failure) for actual server rejections.
                // Re-throw network errors so callers don't clear tokens when
                // the server is simply unreachable.
                if (err?.response) return null;
                throw err;
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    async login(req: LoginRequest): Promise<TokenResponse> {
        const resp = await this.instance.post('/v1/auth/login', req,
            { _skipAuthRefresh: true } as AuthRequestConfig);
        const parsed = tokenResponse.parse(resp.data);
        this.setAccessToken(parsed.accessToken);
        this.setRefreshToken(parsed.refreshToken);
        return parsed;
    }

    async logout(): Promise<void> {
        const refresh = this.refreshToken;
        this.clearTokens();
        if (!refresh) return;
        try {
            await this.instance.post('/v1/auth/logout', { refreshToken: refresh },
                { _skipAuthRefresh: true } as AuthRequestConfig);
        } catch {
            // best-effort
        }
    }

    async tryRestoreSession(): Promise<User | null> {
        if (!this.refreshToken) return null;
        try {
            const token = await this.refreshAccessToken();
            if (!token) {
                this.clearTokens();
                return null;
            }
            const resp = await this.instance.get('/v1/auth/me');
            return user.parse(resp.data);
        } catch (err: any) {
            // Only clear tokens on definitive auth rejections (4xx).
            // Network errors (server down) should preserve tokens so the
            // session can resume when the server comes back.
            if (err?.response?.status >= 400 && err?.response?.status < 500) {
                this.clearTokens();
            }
            return null;
        }
    }

    async getCurrentUser(): Promise<User> {
        const resp = await this.instance.get('/v1/auth/me');
        return user.parse(resp.data);
    }

    async changePassword(req: ChangePasswordRequest): Promise<TokenResponse> {
        const resp = await this.instance.post('/v1/auth/change-password', req);
        const parsed = tokenResponse.parse(resp.data);
        this.setAccessToken(parsed.accessToken);
        this.setRefreshToken(parsed.refreshToken);
        return parsed;
    }

    // -------------------- Users (admin) --------------------

    async listUsers(): Promise<ListUsersResponse> {
        const resp = await this.instance.get('/v1/users');
        return listUsersResponse.parse(resp.data);
    }

    async getUser(id: number): Promise<User> {
        const resp = await this.instance.get(`/v1/users/${id}`);
        return user.parse(resp.data);
    }

    async createUser(req: CreateUserRequest): Promise<User> {
        const resp = await this.instance.post('/v1/users', req);
        return user.parse(resp.data);
    }

    async updateUser(id: number, req: UpdateUserRequest): Promise<User> {
        const resp = await this.instance.patch(`/v1/users/${id}`, req);
        return user.parse(resp.data);
    }

    async sendPasswordReset(id: number): Promise<void> {
        await this.instance.post(`/v1/users/${id}/send-password-reset`);
    }

    async resetPassword(token: string, password: string): Promise<void> {
        await this.instance.post('/v1/auth/reset-password',
            { token, password },
            { _skipAuthRefresh: true } as AuthRequestConfig);
    }

    async listRules(): Promise<ListRulesResponse> {
        return listRulesResponse.parse((await this.instance.get('/v1/rules')).data);
    }

    async dashboardStats(dateStart: Date, dateEnd: Date): Promise<DashboardStats> {
        const resp = await this.instance.get('/v1/dashboard', {
            params: {
                startDate: dayjs(dateStart).utc(true).startOf('day').toISOString(),
                endDate: dayjs(dateEnd).utc(true).endOf('day').toISOString(),
            }
        });
        return dashboardStats.parse(resp.data);
    }
    
    async listPortfolio(pagination: Pagination, req: ListPortfolioRequest): Promise<ListPortfolioResponse> {
        const resp = await this.instance.get('/v1/portfolio', {
            params: {page: pagination.page, pageSize: pagination.pageSize, search: req?.search}
        });
        return listPortfolioResponse.parse(resp.data);
    }

    getListDomainLeadsParams(req: ListDomainLeadsRequest) {
        return {search: req?.search, pickStatus: req?.pickStatus?.join(','), portfolio: req.portfolio, picksToSendOnly: req.picksToSendOnly,
            badNS: req.filterBadNS, clientHold: req.clientHold ? 'true': undefined, tags: req.tags?.join(','),
            needsPricing: req.needsPricing};
    }

    async getJob(jobId: number): Promise<Job> {
        const resp = await this.instance.get('/v1/jobs/' + encodeURIComponent(jobId));
        return job.parse(resp.data);
    }

    async getDomain(domain: string): Promise<DomainInfo|undefined> {
        try {
            const resp = await this.instance.get('/v1/domain/' + encodeURIComponent(domain));
            return domainInfo.parse(resp.data);
        }
        catch(e) {
            if(e instanceof AxiosError) {
                if(e.response?.status == 404) {
                    return undefined;
                }
            }
            else {
                throw e;
            }
        }
    }

    async listJobs(pagination: Pagination, req: ListJobsRequest): Promise<ListJobsResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize, type: req?.type?.join(",")};
        if(req?.status) {
            params.status = req.status;
        }
        const resp = await this.instance.get('/v1/jobs', {
            params
        });
        return listJobsResponse.parse(resp.data);
    }

    async listTags(): Promise<ListTagsResponse> {
        const resp = await this.instance.get('/v1/tags');
        return listTagsResponse.parse(resp.data);
    }

    async setDomainTags(domain: string, req: SetDomainTagsRequest) {
        await this.instance.post('v1/domain/' + encodeURIComponent(domain) + '/tags', req);
    }

    async getEmail(id: number): Promise<InboundEmail> {
        const resp = await this.instance.get('/v1/inboundEmails/' + encodeURIComponent(id));
        return inboundEmail.parse(resp.data);
    }

    async deleteEmailTag(emailId: number, tag: string): Promise<void> {
        await this.instance.delete('/v1/inboundEmails/' + encodeURIComponent(emailId) + '/tags/' + encodeURIComponent(tag));
    }

    async listEmails(pagination: Pagination, req: ListEmailsRequest): Promise<ListEmailsResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};

        if(req.tag) {
            params.tag = req.tag;
        }
        if(req.search) params.search = req.search;

        const resp = await this.instance.get('/v1/inboundEmails', {params});
        return listEmailsResponse.parse(resp.data);
    }

    async getListings(pagination: Pagination): Promise<GetListingsResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};
        const resp = await this.instance.get('/v1/sales/listings', {params});
        return getListingsResponse.parse(resp.data);
    }

    async getListingActivity(pagination: Pagination, req?: GetListingActivityRequest): Promise<GetListingActivityResponse> {
        var params: any = {page: pagination.page, pageSize: pagination.pageSize};
        if(req) {
            params = {...params, ...req};
        }
        const resp = await this.instance.get('/v1/sales/activity', {params});
        return getListingActivityResponse.parse(resp.data);
    }

    async listDomainLeads(pagination: Pagination, req: ListDomainLeadsRequest): Promise<ListDomainLeadsResponse> {
        const params: any = {...this.getListDomainLeadsParams(req), pageSize: pagination.pageSize};
        if(pagination.token) {
            params.token = pagination.token;
        }
        const resp = await this.instance.post('/v1/domainLeads', {
            domains: req.domains,
            tagAll: req.tagAll,
        }, {
            params,
        });
        return listDomainLeadsResponse.parse(resp.data);
    }

    async listNCAftermarketSales(req: ListNCAftermarketSalesRequest): Promise<ListNCAftermarketSalesResponse> {
        const params: any = {};
        if (req.pageSize !== undefined) params.pageSize = req.pageSize;
        if (req.cursor) params.cursor = req.cursor;
        if (req.orderBy) params.orderBy = req.orderBy;
        if (req.direction) params.direction = req.direction;
        if (req.name) params.name = req.name;
        if (req.tld) params.tld = req.tld;
        if (req.keywords) params.keywords = req.keywords;
        if (req.price) params.price = req.price;
        if (req.nameLength) params.nameLength = req.nameLength;
        if (req.bidCount) params.bidCount = req.bidCount;
        if (req.startDate) params.startDate = req.startDate;
        if (req.endDate) params.endDate = req.endDate;
        if (req.noHyphens) params.noHyphens = 'true';
        if (req.noNumbers) params.noNumbers = 'true';
        if (req.onlyNumbers) params.onlyNumbers = 'true';
        const resp = await this.instance.get('/v1/ncAftermarket/sales', { params });
        return listNCAftermarketSalesResponse.parse(resp.data);
    }

    async getNCAftermarketSale(id: string): Promise<NCAftermarketSale> {
        const resp = await this.instance.get('/v1/ncAftermarket/sales/' + encodeURIComponent(id));
        return ncAftermarketSale.parse(resp.data);
    }

    async listNCAftermarketBids(req: ListNCAftermarketBidsRequest): Promise<ListNCAftermarketBidsResponse> {
        const params: any = {};
        if (req.page !== undefined) params.page = req.page;
        if (req.pageSize !== undefined) params.pageSize = req.pageSize;
        if (req.sale) params.sale = req.sale;
        const resp = await this.instance.get('/v1/ncAftermarket/bids', { params });
        return listNCAftermarketBidsResponse.parse(resp.data);
    }

    async listDomainRenewalBlacklist(pagination: Pagination, req: ListDomainRenewalBlacklistRequest): Promise<ListDomainRenewalBlacklistResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};
        if(req.search) params['search'] = req.search;
        return listDomainRenewalBlacklistResponse.parse((await this.instance.post('/v1/renewalBlacklist/search', {
            domains: req.domains,
        }, {params})).data);
    }
    
    async batchAddDomainRenewalBlacklist(formData: FormData): Promise<void> {
        await this.instance.post('/v1/renewalBlacklist', formData, {
            headers: {"Content-Type": 'multipart/form-data'},
        });
    }

    async batchDeleteDomainRenewalBlacklist(formData: FormData): Promise<void> {
        await this.instance.delete('/v1/renewalBlacklist', {
            headers: {"Content-Type": 'multipart/form-data'},
            data: formData
        });
    }

    async getNotifications(): Promise<GetNotificationsResponse> {
        return getNotificationsResponse.parse((await this.instance.get('/v1/notifications')).data);
    }

    downloadDomainLeads(req: ListDomainLeadsRequest): string {
        const params = {...this.getListDomainLeadsParams(req), download: true};
        return API_BASE_URL + '/v1/domainLeads?' + Object.entries(params).map(([k,v]) => k + "=" + encodeURIComponent(v||'')).join("&");
    }

    async uploadSalesHistory(req: UploadSalesHistoryRequest): Promise<UploadSalesHistoryResponse> {
        const resp = await this.instance.post('/v1/sales/loadHistoricalData', {
            file: req.file,
        }, {
            headers: {
                "Content-Type": "multipart/form-data"
            },
            params: {
                fileType: req.fileType,
                account: req.account,
            }
        });
        return uploadSalesHistoryResponse.parse(resp.data);
    }

    getAttachmentDownloadUrl(attachment: InboundEmailAttachment) {
        return API_BASE_URL + '/v1/inboundEmailAttachments/' + encodeURIComponent(attachment.id) + '/download';
    }

    // NC API Accounts
    async listNCAPIAccounts(): Promise<ListNCAPIAccountsResponse> {
        const resp = await this.instance.get('/v1/nc-accounts');
        return listNCAPIAccountsResponse.parse(resp.data);
    }

    async getNCAPIAccount(id: number): Promise<NCAPIAccount> {
        const resp = await this.instance.get(`/v1/nc-accounts/${id}`);
        return ncAPIAccount.parse(resp.data);
    }

    async createNCAPIAccount(req: CreateNCAPIAccountRequest): Promise<NCAPIAccount> {
        const resp = await this.instance.post('/v1/nc-accounts', req);
        return ncAPIAccount.parse(resp.data);
    }

    async updateNCAPIAccountToken(id: number, req: UpdateNCAPIAccountTokenRequest): Promise<NCAPIAccount> {
        const resp = await this.instance.post(`/v1/nc-accounts/${id}/update-token`, req);
        return ncAPIAccount.parse(resp.data);
    }

    async refreshNCAPIAccountToken(id: number): Promise<NCAPIAccount> {
        const resp = await this.instance.post(`/v1/nc-accounts/${id}/refresh-token`);
        return ncAPIAccount.parse(resp.data);
    }

    async updateNCAPIAccountStatus(id: number, req: UpdateNCAPIAccountStatusRequest): Promise<void> {
        await this.instance.patch(`/v1/nc-accounts/${id}/status`, req);
    }

    async updateNCAPIAccountNotes(id: number, req: UpdateNCAPIAccountNotesRequest): Promise<void> {
        await this.instance.patch(`/v1/nc-accounts/${id}/notes`, req);
    }

    async searchAuditEvents(params: {
        startDate?: Date;
        endDate?: Date;
        eventTypes: string[];
        account?: string;
        domain?: string;
        page?: number;
        pageSize?: number;
    }): Promise<AuditEventSearchResponse> {
        const resp = await this.instance.get('/v1/auditEvents/search', {
            params: {
                startDate: params.startDate?.toISOString(),
                endDate: params.endDate?.toISOString(),
                eventTypes: params.eventTypes.join(','),
                account: params.account,
                domain: params.domain,
                page: params.page || 1,
                pageSize: params.pageSize || 10,
            }
        });
        return auditEventSearchResponse.parse(resp.data);
    }

    getAuditEventsExportUrl(params: {
        startDate?: Date;
        endDate?: Date;
        eventTypes: string[];
        account?: string;
        domain?: string;
    }): string {
        const queryParams: Record<string, string> = {
            eventTypes: params.eventTypes.join(','),
        };
        if (params.startDate) {
            queryParams.startDate = params.startDate.toISOString();
        }
        if (params.endDate) {
            queryParams.endDate = params.endDate.toISOString();
        }
        if (params.account) {
            queryParams.account = params.account;
        }
        if (params.domain) {
            queryParams.domain = params.domain;
        }
        return API_BASE_URL + '/v1/auditEvents/export?' + Object.entries(queryParams)
            .map(([k, v]) => k + '=' + encodeURIComponent(v))
            .join('&');
    }

    async setDomainAutoRenew(domain: string, req: SetDomainAutoRenewRequest): Promise<SetDomainAutoRenewResponse> {
        const resp = await this.instance.post('/v1/domain/' + encodeURIComponent(domain) + '/autorenew', req);
        return setDomainAutoRenewResponse.parse(resp.data);
    }

    async createManualSalesActivity(req: CreateManualSalesActivityRequest): Promise<CreateManualSalesActivityResponse> {
        const resp = await this.instance.post('/v1/sales/activity', req);
        return createManualSalesActivityResponse.parse(resp.data);
    }

    // Alerts
    async listAlerts(pagination: Pagination, req?: ListAlertsRequest): Promise<ListAlertsResponse> {
        const params: any = { page: pagination.page, pageSize: pagination.pageSize };
        if (req?.status) params.status = req.status;
        if (req?.alertTypeId) params.alertTypeId = req.alertTypeId;
        if (req?.search) params.search = req.search;
        const resp = await this.instance.get('/v1/alerts', { params });
        return listAlertsResponse.parse(resp.data);
    }

    async getAlert(id: number): Promise<Alert> {
        const resp = await this.instance.get(`/v1/alerts/${id}`);
        return alert.parse(resp.data);
    }

    async updateAlertNotes(id: number, notes: string): Promise<Alert> {
        const resp = await this.instance.patch(`/v1/alerts/${id}/notes`, { notes });
        return alert.parse(resp.data);
    }

    async dismissAlert(id: number): Promise<Alert> {
        const resp = await this.instance.patch(`/v1/alerts/${id}/dismiss`);
        return alert.parse(resp.data);
    }

    async getAlertHistory(id: number): Promise<AlertHistoryResponse> {
        const resp = await this.instance.get(`/v1/alerts/${id}/history`);
        return alertHistoryResponse.parse(resp.data);
    }

    async getAlertAuditLog(id: number): Promise<AlertAuditLogResponse> {
        const resp = await this.instance.get(`/v1/alerts/${id}/audit-log`);
        return alertAuditLogResponse.parse(resp.data);
    }

    // Alert Types
    async listAlertTypes(): Promise<ListAlertTypesResponse> {
        const resp = await this.instance.get('/v1/alert-types');
        return listAlertTypesResponse.parse(resp.data);
    }

    async updateAlertType(id: number, req: UpdateAlertTypeRequest): Promise<AlertType> {
        const resp = await this.instance.patch(`/v1/alert-types/${id}`, req);
        return alertType.parse(resp.data);
    }
}

const APIClient = new API();

export default APIClient;
